import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import type { DateRangeFilter } from './dateRange';
import { createDateRangeFilter, getDateRangeLabel } from './dateRange';

interface DateRangeFilterDialogProps {
  open: boolean;
  onClose: () => void;
  dateRangeFilter: DateRangeFilter;
  onChange: (filter: DateRangeFilter) => void;
  /** Contextual copy — which surface the range filters. */
  scopeLabel?: string;
}

export function DateRangeFilterDialog({
  open,
  onClose,
  dateRangeFilter,
  onChange,
  scopeLabel = 'dashboard data',
}: DateRangeFilterDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select Date Range</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Choose a date range to filter all {scopeLabel}. Your preference will be saved.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Quick Filters
          </Typography>
          <ToggleButtonGroup
            value={dateRangeFilter.type}
            exclusive
            onChange={(_, newType) => {
              if (newType) {
                onChange(createDateRangeFilter(newType));
              }
            }}
            sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
          >
            <ToggleButton value="two_months" size="small">2 Months (±1)</ToggleButton>
            <ToggleButton value="current_quarter" size="small">Current Quarter</ToggleButton>
            <ToggleButton value="current_year" size="small">Current Year</ToggleButton>
            <ToggleButton value="year_to_date" size="small">Year To Date</ToggleButton>
            <ToggleButton value="last_3_months" size="small">6 Months (±3)</ToggleButton>
            <ToggleButton value="custom" size="small">Custom</ToggleButton>
          </ToggleButtonGroup>

          {dateRangeFilter.type === 'custom' && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <DatePicker
                label="Start Date"
                value={dateRangeFilter.startDate}
                format="dd/MM/yyyy"
                views={['year', 'month', 'day']}
                onChange={(date) => {
                  if (date && !isNaN(date.getTime())) {
                    onChange({
                      ...dateRangeFilter,
                      startDate: date,
                      label: getDateRangeLabel('custom', date, dateRangeFilter.endDate),
                    });
                  }
                }}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
              <DatePicker
                label="End Date"
                value={dateRangeFilter.endDate}
                format="dd/MM/yyyy"
                views={['year', 'month', 'day']}
                onChange={(date) => {
                  if (date && !isNaN(date.getTime())) {
                    onChange({
                      ...dateRangeFilter,
                      endDate: date,
                      label: getDateRangeLabel('custom', dateRangeFilter.startDate, date),
                    });
                  }
                }}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Box>
          )}

          <Box
            sx={(theme) => ({
              p: 2,
              backgroundColor: theme.palette.accent.tint,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
            })}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Selected Range</Typography>
            <Typography variant="body2"><strong>{dateRangeFilter.label}</strong></Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {dateRangeFilter.startDate.toLocaleDateString()} - {dateRangeFilter.endDate.toLocaleDateString()}
            </Typography>
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default DateRangeFilterDialog;
